import EventManager from '../eventManager/EventManager'
import { Slot } from './Slot'
import { SLOT_RENDER_ATTR } from '../constants'
import { AdType } from '../enums/AdType'
import { AuctionHouse } from '../auctionHouse/AuctionHouse'

export namespace SlotRenderer {
  /**
   * Render inserts the template of a Slot instance into the DOM.
   * If the rendering did not work for any reason, it returns false.
   * Otherwise, true.
   */
  export const render = (slot: Slot): boolean => {
    if (
      !slot.validate() ||
      !slot.target ||
      !slot.insertPosition ||
      !slot.template
    ) {
      return false
    }

    slot.target.setAttribute(
      `${SLOT_RENDER_ATTR}-${slot.slotModel.adUnitId}`,
      'true'
    )
    slot.target.insertAdjacentHTML(slot.insertPosition, slot.template())
    EventManager.trigger(EventManager.events.slotWrapperRenderEnded, slot)

    if (slot.refreshTime && slot.refreshTime > 0) {
      slot.resizeObserver = createResizeObserver(slot)
    }

    return true
  }

  /**
   * This watches for subtree changes and resizes our containers if one occurs.
   * Doing this prevents race conditions between our resizing logic and when GPT
   * sizes their slot's iframe during a refresh.
   */
  const createResizeObserver = (slot: Slot) => {
    if (!slot.target) {
      return
    }
    const resizeObserver = new MutationObserver(() => slot.sizeContainers())
    resizeObserver.observe(slot.target, {
      subtree: true,
      childList: true
    })

    return resizeObserver
  }

  /**
   * This function fires when a slotRenderEnded event is emitted from googletag.
   * We can determine if ad is from a Prebid partner or an AdX partner based on
   * the event.size. If the size is [1, 1], we are dealing with a Prebid winner
   * and can skip this logic. Else, we need to handle sizing specific to AdX
   * ads here.
   */
  export const handleAdXAds = (
    {
      size,
      isEmpty
    }: Pick<googletag.events.SlotRenderEndedEvent, 'size' | 'isEmpty'>,
    slot: Slot | undefined,
    auctionHouse: AuctionHouse
  ) => {
    if (!slot) {
      return
    }

    /** An 'isEmpty' event means the auctions had no winner */
    if (isEmpty) {
      slot.wrapper.style.display = 'none'
      return
    }

    /** We've never seen a string returned but the docs suggest its possible */
    if (typeof size === 'string') {
      return
    }

    const [adWidth, adHeight] = size as Array<number>

    // Google won the auction, so we need to size the ad wrapper here
    if (adWidth !== 1 && adHeight !== 1) {
      /** An event.size of [0, 0] represents a Google native advertisement */
      slot.adType =
        adWidth === 0 && adHeight === 0 ? AdType.adXNative : AdType.adXStandard

      slot.width = adWidth
      slot.height = adHeight
      slot.sizeContainers()
      if (slot && slot.highestPrebid) {
        auctionHouse.addBids([slot.highestPrebid])
        slot.highestPrebid = undefined
      }
    }
  }
}
